"use client";

import type { JSX, ReactNode } from "react";
import { Icon, Text, View } from "@/components";
import { useLayout } from "@/providers";
import type { CertificateStatus, CertificateView } from "@/types/elearning";
import { dateForm } from "@/utils/date";
import { i18nEnumKey } from "@/utils/elearning/i18nEnumKey";

export interface CertificateCardProps {
  /** Certificado del titular tal como lo devuelve el servidor. */
  certificate: CertificateView;
  /** Slot de acciones (descarga PDF, enlace de verificación, municipio). */
  children?: ReactNode;
}

const STATUS_STYLES: Record<
  CertificateStatus,
  { icon: string; classes: string }
> = {
  valid: {
    icon: "fa-solid fa-circle-check",
    classes:
      "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
  },
  expired: {
    icon: "fa-regular fa-clock",
    classes:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
  },
  revoked: {
    icon: "fa-solid fa-ban",
    classes: "bg-destructive/10 text-destructive"
  }
};

/**
 * @name CertificateCard
 * @description Ficha del certificado del titular (A8): código público,
 * vigencia (1 año desde la emisión) y estado (valid / expired / revoked).
 * La emisión es automática al aprobar el curso; esta ficha solo refleja el
 * certificado que emitió el servidor.
 *
 * @param {CertificateCardProps} props - Certificado y slot de acciones.
 * @returns {JSX.Element} La ficha renderizada.
 */
const CertificateCard = ({
  certificate,
  children
}: CertificateCardProps): JSX.Element => {
  const { t } = useLayout();
  const status = STATUS_STYLES[certificate.status] ?? STATUS_STYLES.valid;

  return (
    <View className="box p-6 rounded-2xl border border-border flex flex-col gap-5 max-w-2xl">
      <View className="flex items-start justify-between gap-4">
        <View className="flex items-center gap-3">
          <Icon
            name="fa-solid fa-certificate"
            className="text-primary text-2xl"
          />
          <View className="flex flex-col">
            <Text type="h2" className="text-lg font-semibold text-foreground">
              {t("certificate")}
            </Text>
            <Text className="text-xs text-muted-foreground">
              {`${t("certificateCode")}: ${certificate.code}`}
            </Text>
          </View>
        </View>
        <Text
          type="span"
          className={`shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${status.classes}`}
        >
          <Icon name={status.icon} />
          {t(i18nEnumKey("certificateStatus", certificate.status))}
        </Text>
      </View>

      <View className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <View className="flex flex-col">
          <Text className="text-xs text-muted-foreground">
            {t("certificateHolder")}
          </Text>
          <Text className="text-sm font-medium text-foreground">
            {certificate.holderName}
          </Text>
        </View>
        <View className="flex flex-col">
          <Text className="text-xs text-muted-foreground">
            {t("holderDocument")}
          </Text>
          <Text className="text-sm font-medium text-foreground">
            {certificate.holderDocument ?? t("notInformed")}
          </Text>
        </View>
        <View className="flex flex-col">
          <Text className="text-xs text-muted-foreground">{t("issuedAt")}</Text>
          <Text className="text-sm font-medium text-foreground">
            {dateForm(certificate.issuedAt, "shortDate")}
          </Text>
        </View>
        <View className="flex flex-col">
          <Text className="text-xs text-muted-foreground">
            {t("validUntil")}
          </Text>
          <Text className="text-sm font-medium text-foreground">
            {dateForm(certificate.expiresAt, "shortDate")}
          </Text>
        </View>
        <View className="flex flex-col">
          <Text className="text-xs text-muted-foreground">
            {t("presentationProvince")}
          </Text>
          <Text className="text-sm font-medium text-foreground">
            {certificate.targetProvince ?? t("notInformed")}
          </Text>
        </View>
        <View className="flex flex-col">
          <Text className="text-xs text-muted-foreground">
            {t("presentationMunicipality")}
          </Text>
          <Text className="text-sm font-medium text-foreground">
            {certificate.targetMunicipality ?? t("notInformed")}
          </Text>
        </View>
      </View>

      {children && (
        <View className="flex flex-col gap-4 pt-2 border-t border-border">
          {children}
        </View>
      )}
    </View>
  );
};

export default CertificateCard;
